import React, { useCallback, useRef } from 'react';
import t from 'react-translate';
import { isEmpty } from 'underscore';
import { useSelector } from 'react-redux';

// redux
import {
  NQuizQuestion,
  QuizQuestionType,
} from 'redux/schemas/models/quiz';
import { useAppDispatch } from 'redux/store';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { getCourseAliases } from 'redux/selectors/course';
import { getQuizQuestion } from 'redux/selectors/quizzes';

import useForceUpdate from 'shared/hooks/use-force-update';
import ActivityContext, { MatchingAnswerState, QuestionContext } from 'lecture_pages/services/question-set-context';
import { css } from '@emotion/core';
import { gray5 } from 'styles/global_defaults/colors';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import QuizQuestionContent from './quiz-question-content';
import QuizImage from './quiz-image';
import NumberAnswerSection from './question-types/number-answer-question/number-answer-section';
import MultipleChoiceAnswerSection from './question-types/multiple-choice-question/multiple-choice-answer-section';
import ShortAnswerSection from './question-types/short-answer-question/short-answer-section';
import LongAnswerSection from './question-types/long-answer-question/long-answer-section';
import QuizHint from './quiz-hint';
import LikertScaleAnswerSection from './question-types/likert-scale-question/likert-scale-answer-section';
import MatchingQuestionSettingSection from './question-types/matching-question/matching-question-setting-section';
import MatchingQuestionAnswerSection from './question-types/matching-question/matching-question-answer-section';
import TableQuestion from './question-types/table-question/table-question';
import RequiredQuestionSwitch from './required-question-switch';

type QuizQuestionProps = {
  currentQuestion: NQuizQuestion;
  className?: string;
};

const QuizQuestion = (props: QuizQuestionProps) => {
  const { className, currentQuestion } = props;

  const dispatch = useAppDispatch();


  const [isCurrentFeedbackShown, setIsCurrentFeedbackShown] = React.useState(false);
  const forceUpdate = useForceUpdate();

  const {
    reveal,
    closeModal,
    currentQuestionResponse,
    forwardOnQuestionsModalCloseRef,
    setForwardOnQuestionsModalCloseRef,
  } = React.useContext(ActivityContext);

  const {
    answerState,
    setAnswerState,
  } = React.useContext(QuestionContext);

  const {
    isEditMode,
    isAnswerMode,
    isReviewMode,
    isCurrentActivityType,
    isMultipleChoiceQuestion,
    isMatchingQuestion,
  } = useQuestionSetHelpers();

  const aliases = useSelector((state) => getCourseAliases(state));
  const childrenQuestions = useSelector((state) => {
    if (currentQuestion.childrenQuestions && currentQuestion.childrenQuestions.length > 0) {
      return currentQuestion.childrenQuestions.map((childQuestion) => getQuizQuestion(state, childQuestion));
    }
    return [];
  });

  // This state update is to trigger the recalculation of the CTA button position when image is loaded
  const handleImageLoad = () => {
    setTimeout(() => {
      forceUpdate();
    }, 0);
  };

  React.useLayoutEffect(() => {
    if (currentQuestionResponse && isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)) {
      setAnswerState(currentQuestionResponse.feedback.reduce((acc, curr) => ({ ...acc, [curr]: true }), {}));
    }
  }, [currentQuestionResponse, isCurrentActivityType, setAnswerState]);

  const getQuizAnswerSection = useCallback(() => {
    switch (currentQuestion.type) {
      case QuizQuestionType.MULTIPLE_CHOICE:
      case QuizQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER:
        return (
          <MultipleChoiceAnswerSection className={isEditMode ? '' : 'mb-5'} />
        );
      case QuizQuestionType.STATEMENT:
        return null;
      case QuizQuestionType.SHORT_TEXT_QUESTION:
      case QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION:
        return (
          <ShortAnswerSection
            currentQuestion={currentQuestion}
          />
        );
      case QuizQuestionType.LONG_ANSWER_QUESTION:
        return (
          <LongAnswerSection
            currentQuestion={currentQuestion}
          />
        );
      case QuizQuestionType.NUMBER_QUESTION:
        return (
          <NumberAnswerSection
            currentQuestion={currentQuestion}
          />
        );
      case QuizQuestionType.LIKERT_SCALE_QUESTION:
        return (
          <LikertScaleAnswerSection
            currentQuestion={currentQuestion}
          />
        );
      case QuizQuestionType.MATCHING_QUESTION:
        return (
          isEditMode ? <MatchingQuestionSettingSection /> : <MatchingQuestionAnswerSection />
        );
      case QuizQuestionType.TABLE_NUMBER_QUESTION:
      case QuizQuestionType.TABLE_SHORT_ANSWER_QUESTION:
        return (
          <TableQuestion />
        );
      default:
        return null;
    }
  }, [currentQuestion, isEditMode]);

  const isObject = (variable) => typeof variable === 'object' && variable !== null;
  const hasSelectedAtLeastOneOptions = isObject(answerState) ? Object.values(answerState).some((value) => value) : false;

  const showCorrectAnswer = isReviewMode && reveal;

  const isAtLeastOneBottomSectionElementShown = isCurrentFeedbackShown || showCorrectAnswer;
  const canShowQuizImage = currentQuestion.type !== QuizQuestionType.MATCHING_QUESTION
    && (isEditMode || !!currentQuestion.questionFileUpload?.file?.url);

  const quizQuestionRef = useRef(null);

  const styles = css`
    .bottom-section {
      ${!isEditMode && isAtLeastOneBottomSectionElementShown && css`
        border-top: 4px solid ${gray5};
      `};
    }
  `;
  const isCurrentQuestionRequired = () => {
    if (childrenQuestions.length > 0) {
      // marking current question as required if any of its children questions are required
      return childrenQuestions.some((childQuestion) => childQuestion.isRequired);
    }
    return currentQuestion.isRequired;
  };

  const unsubmittedAnswer = useCallback(() => {
    if (currentQuestionResponse) {
      return false;
    }
    if (isMultipleChoiceQuestion) {
      return hasSelectedAtLeastOneOptions;
    }
    if (isMatchingQuestion) {
      return !isEmpty((answerState as MatchingAnswerState)?.pairs);
    }
    return !!answerState;
  }, [
    answerState,
    currentQuestionResponse,
    hasSelectedAtLeastOneOptions,
    isMultipleChoiceQuestion,
    isMatchingQuestion,
  ]);

  React.useEffect(() => {
    if (setForwardOnQuestionsModalCloseRef) {
      if (isAnswerMode && unsubmittedAnswer()) {
        const confirm = () => {
          setForwardOnQuestionsModalCloseRef(() => {}, false);
          closeModal();
        };
        if (forwardOnQuestionsModalCloseRef?.current) {
          setForwardOnQuestionsModalCloseRef(() => {
            dispatch(openConfirmationDialog({
              cancelText: t.FORM.CANCEL(),
              confirmText: t.FORM.YES_SURE(),
              onConfirm: confirm,
              title: t.QUIZZES.ARE_YOU_SURE_TO_EXIT(),
            }));
          }, true);
        }
      } else {
        setForwardOnQuestionsModalCloseRef(() => {}, false);
      }
    }
  }, [
    dispatch,
    closeModal,
    isAnswerMode,
    unsubmittedAnswer,
    forwardOnQuestionsModalCloseRef,
    setForwardOnQuestionsModalCloseRef,
  ]);

  return (
    <div ref={quizQuestionRef} css={styles} className={className}>
      <QuizQuestionContent className='mb-5' />
      {isCurrentActivityType(ComponentType.SURVEY)
        && isCurrentQuestionRequired()
        && !isEditMode && (
          <p className='text-danger mb-1 mt-5 semi-bold'>*{t.QUIZZES.TABLE_QUESTION.REQUIRED()}</p>
      )}
      {canShowQuizImage && (
        <div className='d-flex justify-content-center mb-5'>
          <QuizImage
            handleImageLoad={handleImageLoad}
            className='question-image'
          />
        </div>
      )}
      {getQuizAnswerSection()}
      {isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ) && (
        <QuizHint
          isCurrentFeedbackShown={isCurrentFeedbackShown}
          setIsCurrentFeedbackShown={setIsCurrentFeedbackShown}
        />
      )}
      {isCurrentActivityType(ComponentType.SURVEY)
        && isEditMode
        && (currentQuestion.type !== QuizQuestionType.TABLE_NUMBER_QUESTION)
        && (currentQuestion.type !== QuizQuestionType.TABLE_SHORT_ANSWER_QUESTION)
        && (currentQuestion.type !== QuizQuestionType.STATEMENT)
        && (
          <div className='border-top border-gray-5 mt-5 pt-5 mb-1'>
            <RequiredQuestionSwitch
              label={t.SURVEYS.REQUIRED({
                learnerAlias: aliases.learnersAliases?.learnersAlias,
              })}
              labelClassName='text-gray-1'
            />
          </div>
        )}
    </div>
  );
};

export default QuizQuestion;
